import { addLocation, saveImportRecord } from "@/services/location-service";
import { InboxOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Modal, Upload, Table, Select, Button, Steps, message, Alert, Progress, Space, Typography } from "antd";
import { useState, useMemo } from "react";
import * as XLSX from "xlsx";

const { Dragger } = Upload;
const { Text } = Typography;

// Fields available for mapping
const LOCATION_FIELDS = [
	{ value: "", label: "-- Skip --" },
	{ value: "name", label: "Name", required: true },
	{ value: "address1", label: "Address 1", required: true },
	{ value: "address2", label: "Address 2" },
	{ value: "city", label: "City", required: true },
	{ value: "state", label: "State", required: true },
	{ value: "zip", label: "Zip", required: true },
	{ value: "country", label: "Country" },
	{ value: "store_number", label: "Store Number" },
	{ value: "latitude", label: "Latitude" },
	{ value: "longitude", label: "Longitude" },
	{ value: "square_footage", label: "Square Footage" },
	{ value: "brand", label: "Brand" },
];

const REQUIRED_FIELDS = LOCATION_FIELDS.filter((f) => f.required).map((f) => f.value);

interface ImportLocationsModalProps {
	open: boolean;
	onClose: () => void;
	onImportComplete: () => void;
}

/**
 * Auto-map spreadsheet headers to location fields using fuzzy matching
 */
const autoMapColumns = (headers: string[]): Record<number, string> => {
	const mapping: Record<number, string> = {};
	const aliases: Record<string, string[]> = {
		name: ["name", "location name", "location", "site name", "site", "facility", "facility name"],
		address1: ["address1", "address 1", "address", "street", "street address", "addr1", "addr 1"],
		address2: ["address2", "address 2", "addr2", "addr 2", "suite", "unit", "apt"],
		city: ["city", "town", "municipality"],
		state: ["state", "province", "st", "region"],
		zip: ["zip", "zipcode", "zip code", "postal", "postal code", "postcode"],
		country: ["country", "nation", "country code"],
		store_number: ["store number", "store_number", "store #", "store no", "store", "number", "site number", "site #", "site id", "location id"],
		latitude: ["latitude", "lat"],
		longitude: ["longitude", "lng", "lon", "long"],
		square_footage: ["square footage", "sq ft", "sqft", "square_footage", "area"],
		brand: ["brand", "franchise"],
	};

	const used = new Set<string>();

	headers.forEach((header, idx) => {
		const normalized = header.toLowerCase().trim();
		for (const [field, aliasList] of Object.entries(aliases)) {
			if (!used.has(field) && aliasList.includes(normalized)) {
				mapping[idx] = field;
				used.add(field);
				break;
			}
		}
	});

	return mapping;
};

const ImportLocationsModal = ({ open, onClose, onImportComplete }: ImportLocationsModalProps) => {
	const [step, setStep] = useState(0);
	const [headers, setHeaders] = useState<string[]>([]);
	const [rows, setRows] = useState<string[][]>([]);
	const [columnMapping, setColumnMapping] = useState<Record<number, string>>({});
	const [importing, setImporting] = useState(false);
	const [importProgress, setImportProgress] = useState(0);
	const [importResults, setImportResults] = useState<{ success: number; failed: number; errors: string[] }>({ success: 0, failed: 0, errors: [] });
	const [fileName, setFileName] = useState("");

	const reset = () => {
		setStep(0);
		setHeaders([]);
		setRows([]);
		setColumnMapping({});
		setImporting(false);
		setImportProgress(0);
		setImportResults({ success: 0, failed: 0, errors: [] });
		setFileName("");
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	// Parse uploaded file
	const handleFile = (file: File) => {
		setFileName(file.name);
		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const data = new Uint8Array(e.target?.result as ArrayBuffer);
				const workbook = XLSX.read(data, { type: "array" });
				const sheet = workbook.Sheets[workbook.SheetNames[0]];
				const json: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

				if (json.length < 2) {
					message.error("File must have a header row and at least one data row.");
					return;
				}

				const fileHeaders = json[0].map(String);
				const fileRows = json.slice(1).filter((row) => row.some((cell) => String(cell).trim() !== ""));

				if (fileRows.length === 0) {
					message.error("No data rows found in the file.");
					return;
				}

				setHeaders(fileHeaders);
				setRows(fileRows);
				setColumnMapping(autoMapColumns(fileHeaders));
				setStep(1);
			} catch {
				message.error("Failed to parse file. Please ensure it's a valid Excel or CSV file.");
			}
		};
		reader.readAsArrayBuffer(file);
		return false; // Prevent default upload
	};

	// Check if all required fields are mapped
	const missingRequired = useMemo(() => {
		const mapped = new Set(Object.values(columnMapping));
		return REQUIRED_FIELDS.filter((f) => !mapped.has(f));
	}, [columnMapping]);

	// Preview data using current mapping
	const previewData = useMemo(() => {
		return rows.slice(0, 5).map((row, rowIdx) => {
			const mapped: Record<string, string> = { key: String(rowIdx) };
			Object.entries(columnMapping).forEach(([colIdx, field]) => {
				if (field) {
					mapped[field] = String(row[Number(colIdx)] ?? "");
				}
			});
			return mapped;
		});
	}, [rows, columnMapping]);

	const previewColumns = useMemo(() => {
		return Object.values(columnMapping)
			.filter(Boolean)
			.map((field) => ({
				title: LOCATION_FIELDS.find((f) => f.value === field)?.label || field,
				dataIndex: field,
				key: field,
				ellipsis: true,
				width: 120,
			}));
	}, [columnMapping]);

	// Handle import
	const handleImport = async () => {
		if (missingRequired.length > 0) {
			message.error("Please map all required fields before importing.");
			return;
		}

		setImporting(true);
		setStep(2);
		setImportProgress(0);

		let success = 0;
		let failed = 0;
		const errors: string[] = [];

		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];
			const location: Record<string, any> = {};

			Object.entries(columnMapping).forEach(([colIdx, field]) => {
				if (field) {
					const val = String(row[Number(colIdx)] ?? "").trim();
					if (val) {
						if (field === "latitude" || field === "longitude" || field === "square_footage") {
							location[field] = Number(val) || undefined;
						} else {
							location[field] = val;
						}
					}
				}
			});

			try {
				await addLocation(location);
				success++;
			} catch (err: any) {
				failed++;
				const addr = location.name || location.address1 || `Row ${i + 2}`;
				errors.push(`${addr}: ${err.message || "Unknown error"}`);
			}

			setImportProgress(Math.round(((i + 1) / rows.length) * 100));
		}

		setImportResults({ success, failed, errors });
		setImporting(false);

		// Save import record for tracking
		try {
			await saveImportRecord({
				file_name: fileName,
				total_rows: rows.length,
				success_count: success,
				failed_count: failed,
				errors: errors.slice(0, 50), // Limit stored errors
			});
		} catch {
			// Don't block on tracking failure
			console.error("Failed to save import record");
		}

		if (success > 0) {
			onImportComplete();
		}
	};

	// Update a single column mapping
	const updateMapping = (colIdx: number, field: string) => {
		setColumnMapping((prev) => {
			const next = { ...prev };
			if (field === "") {
				delete next[colIdx];
			} else {
				// Remove any previous use of this field
				for (const key of Object.keys(next)) {
					if (next[Number(key)] === field) {
						delete next[Number(key)];
					}
				}
				next[colIdx] = field;
			}
			return next;
		});
	};

	// Mapping columns table
	const mappingColumns = [
		{
			title: "Spreadsheet Column",
			dataIndex: "header",
			key: "header",
			width: 200,
			render: (text: string) => <Text strong>{text}</Text>,
		},
		{
			title: "Sample Data",
			dataIndex: "sample",
			key: "sample",
			width: 200,
			render: (text: string) => <Text type="secondary">{text}</Text>,
		},
		{
			title: "Maps To",
			dataIndex: "mapping",
			key: "mapping",
			width: 200,
			render: (_: any, record: any) => (
				<Select
					value={columnMapping[record.colIdx] || ""}
					onChange={(val) => updateMapping(record.colIdx, val)}
					options={LOCATION_FIELDS.map((f) => ({
						...f,
						disabled: f.value !== "" && f.value !== columnMapping[record.colIdx] && Object.values(columnMapping).includes(f.value),
					}))}
					style={{ width: "100%" }}
					size="small"
				/>
			),
		},
	];

	const mappingData = headers.map((header, idx) => ({
		key: String(idx),
		colIdx: idx,
		header,
		sample: rows.length > 0 ? String(rows[0][idx] ?? "") : "",
	}));

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			title="Import Locations from Spreadsheet"
			width={720}
			footer={
				step === 0 ? null : step === 1 ? (
					<Space>
						<Button onClick={() => setStep(0)}>Back</Button>
						<Button
							type="primary"
							onClick={handleImport}
							disabled={missingRequired.length > 0}
							style={{ backgroundColor: "#92278F", borderColor: "#92278F" }}
						>
							Import {rows.length} Location{rows.length !== 1 ? "s" : ""}
						</Button>
					</Space>
				) : (
					<Button onClick={handleClose} type="primary" style={{ backgroundColor: "#92278F", borderColor: "#92278F" }}>
						Done
					</Button>
				)
			}
			destroyOnClose
		>
			<Steps
				current={step}
				size="small"
				style={{ marginBottom: 24 }}
				items={[
					{ title: "Upload" },
					{ title: "Map Columns" },
					{ title: "Import" },
				]}
			/>

			{/* Step 1: Upload */}
			{step === 0 && (
				<Dragger
					accept=".xlsx,.xls,.csv"
					beforeUpload={handleFile}
					showUploadList={false}
					style={{ padding: "20px 0" }}
				>
					<p className="ant-upload-drag-icon">
						<InboxOutlined style={{ color: "#92278F", fontSize: 48 }} />
					</p>
					<p className="ant-upload-text">Click or drag a file to upload</p>
					<p className="ant-upload-hint">Supports .xlsx, .xls, and .csv files. First row must be column headers.</p>
				</Dragger>
			)}

			{/* Step 2: Map Columns */}
			{step === 1 && (
				<div>
					<div style={{ marginBottom: 12 }}>
						<Text type="secondary">
							File: <Text strong>{fileName}</Text> — {rows.length} row{rows.length !== 1 ? "s" : ""} found
						</Text>
					</div>

					{missingRequired.length > 0 && (
						<Alert
							type="warning"
							showIcon
							style={{ marginBottom: 12 }}
							message={`Required fields not mapped: ${missingRequired.map((f) => LOCATION_FIELDS.find((lf) => lf.value === f)?.label).join(", ")}`}
						/>
					)}

					<Table
						dataSource={mappingData}
						columns={mappingColumns}
						pagination={false}
						size="small"
						style={{ marginBottom: 16 }}
						scroll={{ y: 260 }}
					/>

					{previewColumns.length > 0 && (
						<>
							<Text strong style={{ display: "block", marginBottom: 8 }}>Preview (first 5 rows):</Text>
							<Table
								dataSource={previewData}
								columns={previewColumns}
								pagination={false}
								size="small"
								scroll={{ x: "max-content" }}
							/>
						</>
					)}
				</div>
			)}

			{/* Step 3: Import Progress / Results */}
			{step === 2 && (
				<div style={{ textAlign: "center", padding: "20px 0" }}>
					{importing ? (
						<>
							<Progress
								type="circle"
								percent={importProgress}
								strokeColor="#92278F"
							/>
							<p style={{ marginTop: 16, color: "#888" }}>
								Importing locations... {Math.round((importProgress / 100) * rows.length)} of {rows.length}
							</p>
						</>
					) : (
						<>
							<div style={{ marginBottom: 16 }}>
								{importResults.success > 0 && (
									<div style={{ marginBottom: 8 }}>
										<CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18, marginRight: 8 }} />
										<Text strong>{importResults.success}</Text> location{importResults.success !== 1 ? "s" : ""} imported successfully
									</div>
								)}
								{importResults.failed > 0 && (
									<div>
										<CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 18, marginRight: 8 }} />
										<Text strong>{importResults.failed}</Text> location{importResults.failed !== 1 ? "s" : ""} failed
									</div>
								)}
							</div>
							{importResults.errors.length > 0 && (
								<Alert
									type="error"
									showIcon
									message="Import Errors"
									description={
										<div style={{ maxHeight: 150, overflow: "auto", textAlign: "left" }}>
											{importResults.errors.map((err, i) => (
												<div key={i} style={{ fontSize: 12, marginBottom: 4 }}>{err}</div>
											))}
										</div>
									}
								/>
							)}
						</>
					)}
				</div>
			)}
		</Modal>
	);
};

export { ImportLocationsModal };
